import React, { useState, useEffect, useCallback } from 'react'
import Header from 'src/layouts/Header'
import Footer from 'src/layouts/Footer'
import InputCounter from 'src/components/InputCounter'
import ColorBox from 'src/components/ColorBox'
import ImageUpload from 'src/components/ImageUpload'
import MultipleImageUpload from 'src/components/MultipleImageUpload'
import axios from 'src/api/axios'
import { NotificationManager } from 'react-notifications'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import ImageGallery from "react-image-gallery"
import AdTypeBadge from 'src/components/common/AdTypeBadge'
import ArticleList from 'src/components/section/ArticleList'
import moment from 'moment'
import pin from 'src/assets/svg/location.svg'
import eye from 'src/assets/svg/eye.svg'
import time from 'src/assets/svg/time.svg'
import email from 'src/assets/svg/email.svg'
import mobile from 'src/assets/svg/mobile.svg'
import camera from 'src/assets/svg/camera.svg'
import noImage from 'src/assets/svg/no-image.svg'
import { showModal } from 'src/redux/actions/modalActions'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import CountrySelect from 'src/components/CountrySelect'
import PhoneVerificationModal from 'src/components/modals/PhoneVerificationModal'
import useSEO from 'src/hooks/useSEO'
import { useCookies } from 'react-cookie'
import { validateProfileName, validatePersonName } from 'src/utils/nameValidation'

const PostAd = () => {
  useSEO({
    title: 'Post an Ad - SphynxPlace.com',
    description: 'Post your Sphynx cat listing on SphynxPlace.com. Reach thousands of potential buyers and find the perfect home for your Sphynx.',
    keywords: 'post Sphynx ad, sell Sphynx cat, list Sphynx kittens',
    robots: 'noindex, follow',
  });
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const auth = useSelector((state) => state.auth)
  const [cookies] = useCookies(['cookieConsent'])
  const [errors, setErrors] = useState({})
  const [formData, setFormData] = useState({
    ad_type: 'for-sale',
    currency: 'EUR',
  })
  const [adOptions, setAdOptions] = useState({})
  const [showReview, setShowReview] = useState(false)

  // Reset loading states when route changes or component mounts
  useEffect(() => {
    setIsSubmitting(false)
    setIsPublishing(false)
  }, [location.key])

  const [draftArticle, setDraftArticle] = useState(null)
  const [gallery, setGallery] = useState([])
  const [contactData, setContactData] = useState({
    contact_email: '',
    contact_phone: '',
    first_name: '',
    last_name: '',
    city: '',
    country: '',
    zip_code: '',
    account_type: 'individual',
    profile_name: '',
    password: '',
    password_confirmation: ''
  })
  const [contactErrors, setContactErrors] = useState({})
  const [isPublishing, setIsPublishing] = useState(false)
  const [agreeToCreateAccount, setAgreeToCreateAccount] = useState(false)
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false)
  const [isSendingEmailVerification, setIsSendingEmailVerification] = useState(false)
  const [isImageUploading, setIsImageUploading] = useState(false)
  const [isGalleryUploading, setIsGalleryUploading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const imageUploadRef = React.useRef(null)
  const galleryUploadRef = React.useRef(null)

  const fetchAdOptions = useCallback(async () => {
    try {
      const response = await axios.get(`/api/resources/post-ad?ad_type=${formData.ad_type}`)
      setAdOptions(response.data.data)
    } catch (error) {
      NotificationManager.error('Error fetching ad options')
    }
  }, [formData.ad_type])

  useEffect(() => {
    fetchAdOptions()
  }, [fetchAdOptions])

  // When ad_type changes to "for-adoption", clear price
  useEffect(() => {
    if (formData.ad_type === 'for-adoption') {
      setFormData(prev => ({
        ...prev,
        price: '',
        currency: '',
        price_negotiable: false
      }))
    }
    // When ad_type changes to "for-stud", clear female count
    if (formData.ad_type === 'for-stud') {
      setFormData(prev => ({
        ...prev,
        'litter-female-count': ''
      }))
    }
  }, [formData.ad_type])


  const handleChange = useCallback(event => {
    const { name, value, type, checked } = event.target
    setFormData(prevFormData => {
      if (name.endsWith('[]')) {
        const arrayName = name.slice(0, -2)
        const currentArray = prevFormData[arrayName] || []

        if (event.target.checked) {
          setErrors(prev => {
            const newErrors = { ...prev }
            delete newErrors[arrayName]
            return newErrors
          })
          return { ...prevFormData, [arrayName]: [...currentArray, value] }
        } else {
          setErrors(prev => {
            const newErrors = { ...prev }
            delete newErrors[arrayName]
            return newErrors
          })
          return { ...prevFormData, [arrayName]: currentArray.filter(item => item !== value) }
        }
      }
      
      // Handle single checkbox (not array)
      if (type === 'checkbox' && !name.endsWith('[]')) {
        setErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors[name]
          return newErrors
        })
        return { ...prevFormData, [name]: checked }
      }
      
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
      return { ...prevFormData, [name]: value }
    })
  }, [])

  const handleImageChange = useCallback((data) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      featured_image_url: data?.public_url ?? '',
      featured_image: data?.path ?? ''
    }))
    if (data) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.images
        return newErrors
      })
      setContactErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.images
        return newErrors
      })
    }
  }, [])

  const handleGalleryChange = useCallback((data) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      gallery: data.map(item => ({
        id: item.id,
        name: item.name,
        public_url: item.url,
        original_url: item.url,
        preview_url: item.url,
        isNew: item.isNew,
        path: item.isNew ? item.path : undefined
      }))
    }))
    if (data && data.length > 0) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.images
        return newErrors
      })
      setContactErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.images
        return newErrors
      })
    }
  }, [])

  const calculateAge = useCallback((dateOfBirth, adType, ages) => {
    if (adType === 'wanted') {
      if (!ages?.length) return 'Any age'

      // Check if all options are selected
      if (ages.length === 5) return 'Any age'

      // Sort ages by their minimum value
      const sortOrder = {
        '2-4-months': 1,
        '5-12-months': 2,
        '1-2-years': 3,
        '3-5-years': 4,
        'older-than-5-years': 5
      }
      const sortedAges = [...ages].sort((a, b) => sortOrder[a] - sortOrder[b])

      // Check for continuous ranges
      const has24Months = sortedAges.includes('2-4-months')
      const has512Months = sortedAges.includes('5-12-months')
      const has12Years = sortedAges.includes('1-2-years')
      const has35Years = sortedAges.includes('3-5-years')

      // Only combine ranges if they're actually adjacent
      if (has24Months && has512Months && has12Years && has35Years) {
        return '2 months to 5 years'
      }
      if (has512Months && has12Years && has35Years) {
        return '5 months to 5 years'
      }
      if (has24Months && has512Months && has12Years) {
        return '2 months to 2 years'
      }
      if (has512Months && has12Years) {
        return '5 months to 2 years'
      }
      if (has24Months && has512Months) {
        return '2-12 months'
      }
      if (has12Years && has35Years) {
        return '1-5 years'
      }

      // For non-continuous combinations, list them separately
      const ageMap = {
        '2-4-months': '2-4 months',
        '5-12-months': '5-12 months',
        '1-2-years': '1-2 years',
        '3-5-years': '3-5 years',
        'older-than-5-years': 'Older than 5 years'
      }

      return sortedAges.map(age => ageMap[age]).join(', ')
    }

    // For non-wanted ads, keep existing logic
    if (!dateOfBirth) return '-'

    const years = moment().diff(moment(dateOfBirth), 'years')
    const months = moment().diff(moment(dateOfBirth), 'months') % 12
    const days = moment().diff(moment(dateOfBirth), 'days') % 30

    const parts = []
    if (years > 0) parts.push(`${years} years,`)
    if (months > 0) parts.push(`${months} months,`)
    if (days > 0) parts.push(`${days} days`)

    return parts.length > 0 ? parts.join(' ') : '-'
  }, [])

  const performSubmit = useCallback(async () => {
    setIsSubmitting(true)
    // Provjera da li su kolačići prihvaćeni
    if (!cookies.cookieConsent) {
      NotificationManager.error('Please accept cookies to post an ad. Scroll down to see the cookie consent banner.')
      setIsSubmitting(false)
      return
    }
    
    // Cancel any ongoing uploads
    if (imageUploadRef.current) imageUploadRef.current.cancelUpload();
    if (galleryUploadRef.current) galleryUploadRef.current.cancelAllUploads();

    // Frontend validacija za obavezna polja pre kreiranja draft-a
    const validationErrors = {}
    
    // Title je obavezan za sve tipove oglasa
    if (!formData.title || formData.title.trim() === '') {
      validationErrors.title = ['Title is required.']
    }
    
    // For Sale: Title, Price, Date of Birth, Gender, Color
    if (formData.ad_type === 'for-sale') {
      if (!formData['date-of-birth']) {
        validationErrors['date-of-birth'] = ['Date of Birth is required for For Sale ads.']
      }
      if (!formData.color || formData.color.length === 0) {
        validationErrors.color = ['Color is required for For Sale ads.']
      }
      const maleCount = parseInt(formData['litter-male-count'] || 0)
      const femaleCount = parseInt(formData['litter-female-count'] || 0)
      if (maleCount === 0 && femaleCount === 0) {
        validationErrors.gender = ['Gender is required. Please specify at least one male or female.']
      }
    }
    
    // For Adoption: Title, Gender, Date of Birth
    if (formData.ad_type === 'for-adoption') {
      if (!formData['date-of-birth']) {
        validationErrors['date-of-birth'] = ['Date of Birth is required for For Adoption ads.']
      }
      const maleCount = parseInt(formData['litter-male-count'] || 0)
      const femaleCount = parseInt(formData['litter-female-count'] || 0)
      if (maleCount === 0 && femaleCount === 0) {
        validationErrors.gender = ['Gender is required. Please specify at least one male or female.']
      }
    }
    
    // For Stud: Title, Stud Fee, Gender, Date of Birth
    if (formData.ad_type === 'for-stud') {
      if (!formData['date-of-birth']) {
        validationErrors['date-of-birth'] = ['Date of Birth is required for For Stud ads.']
      }
      const maleCount = parseInt(formData['litter-male-count'] || 0)
      if (maleCount === 0) {
        validationErrors.gender = ['Gender is required. Please specify at least one male for For Stud ads.']
      }
    }
    
    // Price validation: All ad types except for-adoption require price or negotiable
    if (formData.ad_type !== 'for-adoption') {
      const hasPrice = formData.price && parseFloat(formData.price) > 0
      const isNegotiable = formData.price_negotiable === true
      if (!hasPrice && !isNegotiable) {
        const priceLabel = formData.ad_type === 'wanted' ? 'budget' : (formData.ad_type === 'for-stud' ? 'stud fee' : 'price')
        validationErrors.price = [`Please enter a ${priceLabel} or mark as Negotiable.`]
      }
    }

    // Image validation: For Sale, For Adoption, For Stud must have at least one image
    const adTypesRequiringImage = ['for-sale', 'for-adoption', 'for-stud']
    if (adTypesRequiringImage.includes(formData.ad_type)) {
      const hasImage = formData.featured_image_url || (formData.gallery && formData.gallery.length > 0)
      if (!hasImage) {
        const adTypeLabel = {
          'for-sale': 'For Sale',
          'for-adoption': 'For Adoption',
          'for-stud': 'For Stud'
        }[formData.ad_type]
        validationErrors.images = [`At least one image is required for ${adTypeLabel} ads.`]
      }
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      setIsSubmitting(false)
      
      // Fokusiraj na prvo polje sa greškom
      const firstErrorField = Object.keys(validationErrors)[0]
      if (firstErrorField) {
        // Mapiranje polja na name atribute
        const fieldMap = {
          'gender': 'litter-male-count',
          'price': 'price',
          'images': 'media-upload-section'
        }
        const fieldName = fieldMap[firstErrorField] || firstErrorField
        const element = document.querySelector(`[name="${fieldName}"]`) || document.querySelector(`[name="${fieldName}[]"]`) || document.getElementById(fieldName)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
          if (element.focus) element.focus({ preventScroll: true })
        }
      }
      
      // Prikaži prvu grešku kao notifikaciju
      const firstError = Object.values(validationErrors)[0]
      if (firstError && firstError[0]) {
        NotificationManager.error(firstError[0])
      }
      return
    }
    
    const dataToSend = { ...formData }
    if (formData.ad_type === 'for-adoption') {
      delete dataToSend.price
      delete dataToSend.currency
      delete dataToSend.price_negotiable
    }

    if (draftArticle && draftArticle.id) {
      dataToSend.id = draftArticle.id
    }

    // Clean up gallery for backend (only send necessary info)
    if (dataToSend.gallery) {
      dataToSend.gallery = dataToSend.gallery.map(item => ({
        id: item.id,
        path: item.path,
        isNew: item.isNew
      }))
    }
    
    try {
      const response = await axios.post('/api/post-ad', dataToSend, {
        disableNotification: true // Disable interceptor for this call, we handle errors manually
      })

      if (response.status === 201 || response.status === 200) {
        if (response.status === 201 && !draftArticle) {
          NotificationManager.success('Ad saved as draft. Please review and publish it.')
        }
        const article = response.data.data
        setDraftArticle(article)
        
        // Ažuriraj formData sa podacima sa servera (ovo čisti isNew i postavlja prave ID-eve)
        setFormData(prev => ({
          ...prev,
          featured_image: article.featured_image_id || prev.featured_image,
          featured_image_url: article.featured_image_url || prev.featured_image_url,
          gallery: article.gallery ? (Array.isArray(article.gallery) ? article.gallery : Object.values(article.gallery)).map(img => ({
            id: img.id,
            path: img.path,
            url: img.original_url || img.public_url || img.url,
            name: img.name,
            isNew: false
          })) : prev.gallery
        }))
        
        // Build gallery for preview screen
        const galleryImages = []
        if (article.gallery) {
          Object.values(article.gallery).forEach(image => {
            galleryImages.push({
              original: image.original_url,
              thumbnail: image.original_url
            })
          })
        }
        if (article.featured_image_url) {
          galleryImages.unshift({
            original: article.featured_image_url,
            thumbnail: article.featured_image_url
          })
        }
        setGallery(galleryImages)
        
        // Fetch ad options for labels if not already loaded
        if (!adOptions || Object.keys(adOptions).length === 0) {
          try {
            const optionsResponse = await axios.get(`/api/resources/post-ad?ad_type=${formData.ad_type}`)
            setAdOptions(optionsResponse.data.data)
          } catch (error) {
            console.error('Error fetching ad options:', error)
          }
        }
        
        // Populate contact data if logged in
        if (auth.isLoggedIn && auth.user) {
          setContactData(prev => ({
            ...prev,
            contact_email: auth.user.email || '',
            contact_phone: auth.user.phone?.number || '',
            first_name: auth.user.first_name || '',
            last_name: auth.user.last_name || '',
            city: auth.user.city || '',
            country: auth.user.country?.cca2 || '',
            account_type: auth.user.account_type || 'individual',
            profile_name: auth.user.profile_name || ''
          }))
          // Check if phone is verified
          if (auth.user.phone?.is_verified) {
            setPhoneVerified(true)
          }
        }
        
        setShowReview(true)
        // Scroll to top when review screen is shown
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 100)
      }
    } catch (error) {
      if (error.response?.status === 422) {
        const errorData = error.response.data
        const errors = errorData.errors || {}
        setErrors(errors)
        const firstErrorField = Object.keys(errors)[0]
        if (firstErrorField) {
          const element = document.querySelector(`[name="${firstErrorField}"]`)
          if (element) element.focus()
        }
        
        // Prikaži glavnu poruku greške ili prvu grešku iz errors objekta
        if (errorData.message) {
          NotificationManager.error(errorData.message)
        } else if (Object.keys(errors).length > 0) {
          const firstError = errors[firstErrorField]
          NotificationManager.error(Array.isArray(firstError) ? firstError[0] : firstError)
        }
      } else {
        NotificationManager.error(error.response?.data?.message || 'Error creating ad')
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [formData, auth, cookies.cookieConsent, adOptions, navigate, dispatch, draftArticle])

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault()
    
    const isAnyImageUploading = isImageUploading || isGalleryUploading
    if (isAnyImageUploading) {
      dispatch(showModal('CONFIRMATION_MODAL', {
        title: 'Upload in Progress',
        message: 'Some images are still being uploaded. Would you like to wait for all uploads to complete, or submit the ad now (incomplete uploads will not be saved)?',
        confirmText: 'Submit anyway',
        cancelText: 'Wait',
        iconType: 'warning',
        onConfirm: performSubmit
      }))
      return
    }

    performSubmit()
  }, [isImageUploading, isGalleryUploading, performSubmit, dispatch])

  useEffect(() => {
    // Populate contact data if logged in
    if (auth.isLoggedIn && auth.user && showReview) {
      const userPhone = auth.user.phone?.number || ''
      setContactData(prev => ({
        ...prev,
        contact_email: prev.contact_email || auth.user.email || '',
        contact_phone: prev.contact_phone || userPhone,
        first_name: prev.first_name || auth.user.first_name || '',
        last_name: prev.last_name || auth.user.last_name || '',
        city: prev.city || auth.user.city || '',
        country: prev.country || auth.user.country?.cca2 || '',
        account_type: prev.account_type || auth.user.account_type || 'individual',
        profile_name: prev.profile_name || auth.user.profile_name || ''
      }))
      // Check if phone is verified - only if contact phone matches user's verified phone
      if (auth.user.phone?.is_verified && userPhone) {
        setPhoneVerified(true)
      }
    }
  }, [auth.isLoggedIn, auth.user, showReview])

  const handleContactChange = useCallback((e) => {
    const { name, value } = e.target
    const inputElement = e.target
    const cursorPosition = inputElement.selectionStart
    
    // Apply name validation for specific fields
    if (name === 'first_name' || name === 'last_name') {
      const result = validatePersonName(value)
      
      // Calculate new cursor position by sanitizing the part before cursor
      const beforeCursor = value.substring(0, cursorPosition)
      const sanitizedBeforeCursor = validatePersonName(beforeCursor).sanitizedValue
      const newCursorPosition = sanitizedBeforeCursor.length
      
      setContactData(prev => ({
        ...prev,
        [name]: result.sanitizedValue
      }))
      
      // Restore cursor position after render
      requestAnimationFrame(() => {
        if (inputElement && inputElement === document.activeElement) {
          inputElement.setSelectionRange(newCursorPosition, newCursorPosition)
        }
      })
      
      if (result.error) {
        setContactErrors(prev => ({
          ...prev,
          [name]: [result.error]
        }))
      } else if (contactErrors[name]) {
        setContactErrors(prev => ({
          ...prev,
          [name]: null
        }))
      }
      return
    }
    
    if (name === 'profile_name') {
      const result = validateProfileName(value)
      
      // Calculate new cursor position by sanitizing the part before cursor
      const beforeCursor = value.substring(0, cursorPosition)
      const sanitizedBeforeCursor = validateProfileName(beforeCursor).sanitizedValue
      const newCursorPosition = sanitizedBeforeCursor.length
      
      setContactData(prev => ({
        ...prev,
        [name]: result.sanitizedValue
      }))
      
      // Restore cursor position after render
      requestAnimationFrame(() => {
        if (inputElement && inputElement === document.activeElement) {
          inputElement.setSelectionRange(newCursorPosition, newCursorPosition)
        }
      })
      
      if (result.error) {
        setContactErrors(prev => ({
          ...prev,
          [name]: [result.error]
        }))
      } else if (contactErrors[name]) {
        setContactErrors(prev => ({
          ...prev,
          [name]: null
        }))
      }
      return
    }
    
    setContactData(prev => ({
      ...prev,
      [name]: value
    }))
    if (contactErrors[name]) {
      setContactErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
    // Reset phone verification if phone number changes
    if (name === 'contact_phone') {
      setPhoneVerified(false)
    }
  }, [contactErrors])


  const handlePublish = useCallback(async (e) => {
    e.preventDefault()
    
    // Provjera da li su kolačići prihvaćeni
    if (!cookies.cookieConsent) {
      NotificationManager.error('Please accept cookies to post an ad. Scroll down to see the cookie consent banner.')
      return
    }
    
    setIsPublishing(true)
    setContactErrors({})

    // Frontend validacija za country
    let countryError = null
    if (auth.isLoggedIn && auth.user?.email) {
      // Za prijavljene korisnike, proveri da li imaju country u profilu ili da li je poslat kroz formu
      if (!contactData.country && !auth.user?.country?.cca2) {
        countryError = 'Country is required to publish an ad.'
      }
    } else {
      // Za neprijavljene korisnike, country je obavezan
      if (!contactData.country) {
        countryError = 'Country is required.'
      }
    }

    if (countryError) {
      setContactErrors({ country: [countryError] })
      setIsPublishing(false)
      // Ne prikazujemo NotificationManager.error ovde jer se greška već prikazuje kroz setContactErrors
      return
    }

    // Frontend validacija za slike - For Sale, For Adoption, For Stud moraju imati minimalno jednu sliku
    const adTypesRequiringImage = ['for-sale', 'for-adoption', 'for-stud']
    if (adTypesRequiringImage.includes(formData.ad_type)) {
      // Provjeri featured slike iz draftArticle (sa servera) ili iz formData (trenutno stanje)
      const hasFeaturedImage = draftArticle.featured_image_url || formData.featured_image_url
      
      // Provjeri galeriju - može biti array ili objekt
      const draftGallery = draftArticle.gallery
      const hasGalleryImages = (gallery && gallery.length > 0) ||
                               (formData.gallery && formData.gallery.length > 0) ||
                               (Array.isArray(draftGallery) && draftGallery.length > 0) ||
                               (draftGallery && typeof draftGallery === 'object' && Object.keys(draftGallery).length > 0)
      
      const hasImage = hasFeaturedImage || hasGalleryImages
      
      if (!hasImage) {
        const adTypeLabel = {
          'for-sale': 'For Sale',
          'for-adoption': 'For Adoption',
          'for-stud': 'For Stud'
        }[formData.ad_type]
        const errorMessage = `At least one image is required for ${adTypeLabel} ads.`
        setContactErrors({ images: [errorMessage] })
        NotificationManager.error(errorMessage)
        setIsPublishing(false)
        return
      }
    }

    // Frontend validacija za obavezna polja
    const validationErrors = {}
    
    // For Sale: Title, Price, Date of Birth, Gender, Color
    if (formData.ad_type === 'for-sale') {
      if (!formData['date-of-birth']) {
        validationErrors['date-of-birth'] = ['Date of Birth is required for For Sale ads.']
      }
      if (!formData.color || formData.color.length === 0) {
        validationErrors.color = ['Color is required for For Sale ads.']
      }
      const maleCount = parseInt(formData['litter-male-count'] || 0)
      const femaleCount = parseInt(formData['litter-female-count'] || 0)
      if (maleCount === 0 && femaleCount === 0) {
        validationErrors.gender = ['Gender is required. Please specify at least one male or female.']
      }
    }
    
    // For Adoption: Title, Gender, Date of Birth
    if (formData.ad_type === 'for-adoption') {
      if (!formData['date-of-birth']) {
        validationErrors['date-of-birth'] = ['Date of Birth is required for For Adoption ads.']
      }
      const maleCount = parseInt(formData['litter-male-count'] || 0)
      const femaleCount = parseInt(formData['litter-female-count'] || 0)
      if (maleCount === 0 && femaleCount === 0) {
        validationErrors.gender = ['Gender is required. Please specify at least one male or female.']
      }
    }
    
    // For Stud: Title, Stud Fee, Gender, Date of Birth
    if (formData.ad_type === 'for-stud') {
      if (!formData['date-of-birth']) {
        validationErrors['date-of-birth'] = ['Date of Birth is required for For Stud ads.']
      }
      const maleCount = parseInt(formData['litter-male-count'] || 0)
      if (maleCount === 0) {
        validationErrors.gender = ['Gender is required. Please specify at least one male for For Stud ads.']
      }
    }
    
    // Wanted: Title (već je obavezno)
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      setIsPublishing(false)

      // Fokusiraj na prvo polje sa greškom
      const firstErrorField = Object.keys(contactErrors).length > 0 ? Object.keys(contactErrors)[0] : Object.keys(validationErrors)[0]
      if (firstErrorField) {
        const fieldMap = {
          'gender': 'litter-male-count',
          'price': 'price',
          'images': 'media-upload-section'
        }
        const fieldName = fieldMap[firstErrorField] || firstErrorField
        const element = document.querySelector(`[name="${fieldName}"]`) || document.querySelector(`[name="${fieldName}[]"]`) || document.getElementById(fieldName)
        
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
          if (element.focus) element.focus({ preventScroll: true })
        } else if (firstErrorField === 'images') {
            // Ako smo na Review ekranu i nemamo slike, moramo nazad
            setShowReview(false);
            setTimeout(() => {
                const mediaSection = document.getElementById('media-upload-section');
                if (mediaSection) mediaSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        }
      }

      // Prikaži prvu grešku kao notifikaciju
      const firstError = Object.values(validationErrors)[0]
      if (firstError && firstError[0]) {
        NotificationManager.error(firstError[0])
      }
      return
    }

    try {
      // For logged in users, send all data that the user can change
      // For non-logged in users, send all contact data
      let dataToSend
      if (auth.isLoggedIn && auth.user?.email) {
        dataToSend = { 
          contact_phone: contactData.contact_phone || null,
        }
        // Add additional data (we send them even if they're the same, backend will check for differences)
        if (contactData.first_name !== undefined) dataToSend.first_name = contactData.first_name
        if (contactData.last_name !== undefined) dataToSend.last_name = contactData.last_name
        if (contactData.account_type !== undefined) dataToSend.account_type = contactData.account_type
        if (contactData.profile_name !== undefined) dataToSend.profile_name = contactData.profile_name
        if (contactData.country !== undefined) dataToSend.country = contactData.country
        if (contactData.city !== undefined) dataToSend.city = contactData.city
        if (contactData.zip_code !== undefined) dataToSend.zip_code = contactData.zip_code
      } else {
        dataToSend = contactData
      }

      const response = await axios.post(`api/article/${draftArticle.id}/publish`, dataToSend, {
        disableNotification: true // Disable interceptor notifications, we handle errors manually
      })
      if (response.status === 200) {
        // If a new user was created, automatically log them in
        if (response.data.user && response.data.token) {
          dispatch({ type: 'LOGIN_SUCCESS', payload: response.data })
        }
        NotificationManager.success('Ad successfully published!')
        navigate(`/ad/${draftArticle.id}/${response.data.data.slug}`)
      }
    } catch (error) {
      if (error.response?.status === 422) {
        const errorData = error.response.data
        const errors = errorData.errors || {}
        setContactErrors(errors)
        
        // If email already exists, show login modal
        if (errorData.error === 'email_exists' || errorData.message?.includes('already exists')) {
          NotificationManager.error('An account with this email already exists. Please sign in.')
          dispatch(showModal('LOGIN_MODAL', { initialEmail: contactData.contact_email }))
        } else if (errorData.message) {
          // Prikaži glavnu poruku greške
          NotificationManager.error(errorData.message)
        } else if (Object.keys(errors).length > 0) {
          // Prikaži prvu grešku iz errors objekta
          const firstErrorKey = Object.keys(errors)[0]
          const firstError = errors[firstErrorKey]
          NotificationManager.error(Array.isArray(firstError) ? firstError[0] : firstError)
        }
      } else {
        NotificationManager.error(error.response?.data?.message || 'Error publishing ad')
      }
    } finally {
      setIsPublishing(false)
    }
  }, [draftArticle, contactData, auth, navigate, dispatch, cookies.cookieConsent])

  const handleSendEmailVerification = async () => {
    setIsSendingEmailVerification(true)
    try {
      const response = await axios.post('/api/my/email/send-verification')
      NotificationManager.success(response.data.message || 'Verification email sent successfully')
    } catch (error) {
      NotificationManager.error(error.response?.data?.error || 'Error sending verification email')
    } finally {
      setIsSendingEmailVerification(false)
    }
  }

  // Review screen
  if (showReview && draftArticle) {

    // Prepare article data for ArticleList component
    // ArticleList expects: id, slug, title, ad_type, price_formatted, refreshed_at, 
    // featured_image_url, gallery (array), attributes (object with keys like 'litter-male-count'),
    // short_description, user (object), city, country_name, is_boosted, is_favorited, status
    const galleryArray = draftArticle.gallery ? (Array.isArray(draftArticle.gallery) ? draftArticle.gallery : Object.values(draftArticle.gallery)) : []
    const articlePreviewData = {
      id: draftArticle.id,
      slug: draftArticle.slug || 'preview',
      title: draftArticle.title || '',
      ad_type: draftArticle.ad_type,
      price_formatted: draftArticle.price_formatted || 'negotiable',
      refreshed_at: null,
      short_description: draftArticle.description ? (draftArticle.description.length > 200 ? `${draftArticle.description.substring(0, 200)}...` : draftArticle.description) : '',
      featured_image_url: draftArticle.featured_image_url || null,
      gallery: galleryArray,
      attributes: {
        ...(draftArticle.attributes || {}),
        'litter-male-count': draftArticle.attributes?.['litter-male-count'] || 0,
        'litter-female-count': draftArticle.attributes?.['litter-female-count'] || 0
      },
      user: auth.isLoggedIn && auth.user ? {
        id: auth.user.id,
        slug: auth.user.slug || '',
        profile_name: auth.user.profile_name || `${auth.user.first_name || ''} ${auth.user.last_name || ''}`.trim() || 'User',
        first_name: auth.user.first_name || '',
        last_name: auth.user.last_name || '',
        account_type: auth.user.account_type || 'Individual',
        avatar: auth.user.avatar || null,
        city: auth.user.city || null,
        country: auth.user.country || null
      } : null,
      city: draftArticle.city || (auth.isLoggedIn && auth.user ? auth.user.city : null),
      country_name: draftArticle.country_name || (auth.isLoggedIn && auth.user?.country ? auth.user.country.name : null),
      is_boosted: false,
      is_favorited: false,
      status: null
    }

    return (
      <>
        <Header />
        <div className='bg-[#F7F7F7] py-[20px] md:py-[35px] min-h-screen pb-[80px] md:pb-[35px]'>
          <div className='container mx-auto px-2'>
            <div className="max-w-[953px] mx-auto">
              <div className="bg-white border border-[#EBEBEB] rounded-lg mb-5" style={{ boxShadow: '0px 3px 6px #8D8D8D36' }}>
                <div className='px-2 md:px-6 lg:px-10 py-4 border-b border-[#EBEBEB]'>
                  <p className='my-auto uppercase font-bold text-[20px] md:text-[24px] leading-7'>
                    Ad Preview
                  </p>
                </div>
              
                <div className='px-2 md:px-6 lg:px-10 pt-5 pb-2.5'>
                  <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6" role="alert">
                    <p className="font-bold text-lg uppercase">Almost there! Your ad is saved as a draft.</p>
                    <p className="mt-2">Review your ad below, then <button 
                      onClick={() => document.getElementById('contact-info')?.scrollIntoView({ behavior: 'smooth' })}
                      className="font-bold underline decoration-2 underline-offset-2"
                    >scroll down</button> to enter your contact information and click <b>"PUBLISH AD"</b> to make it live.</p>
                  </div>

                  {/* Ad Preview - Using ArticleList component */}
                  <div className="mb-6">
                    <ArticleList article={articlePreviewData} />
                  </div>

                  {/* Contact information section */}
                  <hr className="my-6" />
                  <div className="mb-8" id="contact-info">
                  <h3 className='font-bold text-[17px] leading-6 pb-[23px]'>Contact Information</h3>
                  
                  {!auth.isLoggedIn ? (
                    <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-800 p-4 mb-6 rounded" role="alert">
                      <p className="text-sm mb-2">Not signed in? You can <button
                        type="button"
                        onClick={() => dispatch(showModal('LOGIN_MODAL'))}
                        className="text-[#832EE6] hover:text-purple-800 underline font-semibold"
                      >
                        sign in here
                      </button> to use your saved information.</p>
                      <p className="text-sm">If you don't have an account, one will be automatically created for you when you publish your ad using the contact information below.</p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 mb-4">
                      Contact information has been automatically filled from your profile. You can modify it if needed.
                    </p>
                  )}

                  <form onSubmit={handlePublish}>
                    <div className="space-y-4">
                      {/* Account Type */}
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="w-full md:w-1/2 md:max-w-[400px]">
                          <div className="relative">  
                            <label htmlFor="account_type" className="absolute top-0 pt-2.5 px-[15px] block text-xs text-[#656565] dark:text-white">Account Type <span className="text-red-500">*</span></label>
                            <select
                              id="account_type"
                              name="account_type"
                              value={contactData.account_type}
                              onChange={handleContactChange}
                              required
                              className="!pt-[30px] !pb-2 bg-white font-medium border border-gray-300 text-[#000] text-base leading-5 rounded-[4px] focus:ring-primary-500 focus:border-primary-500 block w-full p-[15px] dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
                            >
                              <option value="individual">Individual</option>
                              <option value="breeder">Breeder</option>
                              <option value="company">Company</option>
                            </select>
                          </div>
                        </div>
                        <div className="w-full md:w-1/2 md:max-w-[400px]"></div>
                      </div>

                      {/* First Name, Last Name */}
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="w-full md:w-1/2 md:max-w-[400px]">
                          <div className="relative">  
                            <label htmlFor="first_name" className="absolute top-0 pt-2.5 px-[15px] block text-xs text-[#656565] dark:text-white">First Name <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              id="first_name"
                              name="first_name"
                              value={contactData.first_name}
                              onChange={handleContactChange}
                              required
                              className={`!pt-[30px] !pb-2 bg-white font-medium border ${contactErrors.first_name ? 'border-red-500' : 'border-gray-300'} text-[#000] text-base leading-5 rounded-[4px] focus:ring-primary-500 focus:border-primary-500 block w-full p-[15px] dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500 dark:shadow-sm-light`}
                            />
                          </div>
                          {contactErrors.first_name && (
                            <p className="mt-1 text-sm text-red-500">{contactErrors.first_name[0]}</p>
                          )}
                        </div>
                        <div className="w-full md:w-1/2 md:max-w-[400px]">
                          <div className="relative">  
                            <label htmlFor="last_name" className="absolute top-0 pt-2.5 px-[15px] block text-xs text-[#656565] dark:text-white">Last Name <span className="text-red-500">*</span></label>
                            <input
                              type="text"
                              id="last_name"
                              name="last_name"
                              value={contactData.last_name}
                              onChange={handleContactChange}
                              required
                              className={`!pt-[30px] !pb-2 bg-white font-medium border ${contactErrors.last_name ? 'border-red-500' : 'border-gray-300'} text-[#000] text-base leading-5 rounded-[4px] focus:ring-primary-500 focus:border-primary-500 block w-full p-[15px] dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500 dark:shadow-sm-light`}
                            />
                          </div>
                          {contactErrors.last_name && (
                            <p className="mt-1 text-sm text-red-500">{contactErrors.last_name[0]}</p>
                          )}
                        </div>
                      </div>

                      {/* Profile Name (conditional) */}
                      {(contactData.account_type === 'breeder' || contactData.account_type === 'company') && (
                        <div className="flex flex-col md:flex-row gap-4">
                          <div className="w-full md:w-1/2 md:max-w-[400px]">
                            <div className="relative">  
                              <label htmlFor="profile_name" className="absolute top-0 pt-2.5 px-[15px] block text-xs text-[#656565] dark:text-white">
                                {contactData.account_type === 'breeder' ? 'Breeder Name' : 'Company Name'} <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                id="profile_name"
                                name="profile_name"
                                value={contactData.profile_name}
                                onChange={handleContactChange}
                                required
                                className={`!pt-[30px] !pb-2 bg-white font-medium border ${contactErrors.profile_name ? 'border-red-500' : 'border-gray-300'} text-[#000] text-base leading-5 rounded-[4px] focus:ring-primary-500 focus:border-primary-500 block w-full p-[15px] dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500 dark:shadow-sm-light`}
                              />
                            </div>
                            {contactErrors.profile_name && (
                              <p className="mt-1 text-sm text-red-500">{contactErrors.profile_name[0]}</p>
                            )}
                          </div>
                          <div className="w-full md:w-1/2 md:max-w-[400px]"></div>
                        </div>
                      )}

                      {/* Email */}
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="w-full md:w-1/2 md:max-w-[400px]">
                          <div className="relative">  
                            <label htmlFor="contact_email" className="absolute top-0 pt-2.5 px-[15px] block text-xs text-[#656565] dark:text-white">Email address <span className="text-red-500">*</span></label>
                            <input
                              type="email"
                              id="contact_email"
                              name="contact_email"
                              value={contactData.contact_email}
                              onChange={handleContactChange}
                              required
                              disabled={auth.isLoggedIn && auth.user?.email}
                              className={`!pt-[30px] !pb-2 bg-white font-medium border ${contactErrors.contact_email ? 'border-red-500' : 'border-gray-300'} text-[#000] text-base leading-5 rounded-[4px] focus:ring-primary-500 focus:border-primary-500 block w-full p-[15px] ${auth.isLoggedIn && auth.user?.email ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                          </div>
                          {contactErrors.contact_email && (
                            <p className="mt-1 text-sm text-red-500">{contactErrors.contact_email[0]}</p>
                          )}
                          {auth.isLoggedIn && auth.user?.email && !auth.user?.has_verified_email && (
                            <div className="mt-2 flex items-center gap-2">
                              <p className="text-sm text-yellow-600 flex-1">
                                Your email address must be verified to receive messages from buyers.
                              </p>
                              <button
                                type="button"
                                onClick={handleSendEmailVerification}
                                disabled={isSendingEmailVerification}
                                className="text-sm text-[#832EE6] hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isSendingEmailVerification ? 'Sending...' : 'Verify Email'}
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="w-full md:w-1/2 md:max-w-[400px]"></div>
                      </div>
                      {!auth.isLoggedIn && contactData.contact_email && (
                        <p className="text-sm text-yellow-600 -mt-2">
                          After registration, verify your email in your account to receive inquiries from buyers.
                        </p>
                      )}

                      {/* Phone */}
                      <div className="flex flex-col md:flex-row gap-2 md:gap-4">
                        <div className="w-full md:w-1/2 md:max-w-[400px]">
                          <div className="relative w-full">
                            <label htmlFor="contact_phone" className="absolute top-0 pt-2.5 px-[15px] block text-xs text-[#656565] dark:text-white z-10">
                              Phone number (Include country code) {phoneVerified && <span className="text-green-600 ml-1">✓</span>}
                            </label>
                            <div className="relative">
                              <PhoneInput
                                country="US"
                                value={contactData.contact_phone}
                                name="contact_phone"
                                onChange={(value) => {
                                  handleContactChange({ target: { name: 'contact_phone', value: value }});
                                  // Only keep verified if it matches user's verified phone number
                                  const userVerifiedPhone = auth.user?.phone?.is_verified ? auth.user.phone.number : null
                                  setPhoneVerified(userVerifiedPhone && value === userVerifiedPhone);
                                }}
                                className={`!pt-[30px] !pb-2 bg-white font-medium border ${phoneVerified ? 'border-green-500' : contactErrors.contact_phone ? 'border-red-500' : 'border-gray-300'} text-[#000] text-base leading-5 rounded-[4px] focus:ring-primary-500 focus:border-primary-500 w-full p-[15px]`}
                              />
                              {contactData.contact_phone && !phoneVerified && (
                                <button
                                  type="button"
                                  onClick={() => setShowPhoneVerificationModal(true)}
                                  className="absolute right-4 text-base leading-4 text-[#832EE6] z-20 hover:underline"
                                  style={{ top: '30px' }}
                                >
                                  Verify
                                </button>
                              )}
                              {phoneVerified && (
                                <div className="absolute right-4 text-green-600 z-20" style={{ top: '38px' }}>
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <PhoneVerificationModal
                              isOpen={showPhoneVerificationModal}
                              onClose={() => setShowPhoneVerificationModal(false)}
                              phoneNumber={contactData.contact_phone}
                              onVerificationSuccess={() => {
                                setPhoneVerified(true)
                              }}
                              isPublic={!auth.isLoggedIn}
                            />
                          </div>
                          {contactErrors.contact_phone && (
                            <p className="mt-1 text-sm text-red-500">{contactErrors.contact_phone[0]}</p>
                          )}
                          {contactData.contact_phone && !phoneVerified && (
                            <p className="mt-2 text-sm text-yellow-600">
                              Your phone number will not be visible on the ad until it is verified.
                            </p>
                          )}
                        </div>
                        <div className="w-full md:w-1/2 md:max-w-[400px]"></div>
                      </div>

                      {/* Country, City */}
                      <div className="flex flex-col md:flex-row gap-4">
                        <div className="w-full md:w-1/2 md:max-w-[400px]">
                          <div className="relative">  
                            <label htmlFor="country" className="absolute top-0 pt-2.5 px-[15px] block text-xs text-[#656565] dark:text-white">
                              {formData.ad_type === 'wanted' ? 'Your Country' : 'Country'} <span className="text-red-500">*</span>
                            </label>
                            <CountrySelect 
                              value={contactData.country}
                              onChange={handleContactChange}
                              name="country"
                              className={`!pt-[30px] !pb-2 bg-transparent font-medium border ${contactErrors.country ? 'border-red-500' : 'border-gray-300'} text-[#000] text-base leading-5 rounded-[4px] focus:ring-primary-500 focus:border-primary-500 block w-full p-[15px] dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500 dark:shadow-sm-light`}
                            />
                          </div>
                          {contactErrors.country && (
                            <p className="mt-1 text-sm text-red-500">{contactErrors.country[0]}</p>
                          )}
                        </div>
                        <div className="w-full md:w-1/2 md:max-w-[400px]">
                          <div className="relative">  
                            <label htmlFor="city" className="absolute top-0 pt-2.5 px-[15px] block text-xs text-[#656565] dark:text-white">City</label>
                            <input
                              type="text"
                              id="city"
                              name="city"
                              value={contactData.city}
                              onChange={handleContactChange}
                              className="!pt-[30px] !pb-2 bg-white font-medium border border-gray-300 text-[#000] text-base leading-5 rounded-[4px] focus:ring-primary-500 focus:border-primary-500 block w-full p-[15px] dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500 dark:shadow-sm-light"
                            />
                          </div>
                        </div>
                      </div>

                      {!auth.isLoggedIn && (
                        <>
                          <div className="flex flex-col md:flex-row gap-4">
                            <div className="w-full md:w-1/2 md:max-w-[400px]">
                              <div className="relative">  
                                <label htmlFor="password" className="absolute top-0 pt-2.5 px-[15px] block text-xs text-[#656565] dark:text-white">Password <span className="text-red-500">*</span></label>
                                <input
                                  type="password"
                                  id="password"
                                  name="password"
                                  value={contactData.password}
                                  onChange={handleContactChange}
                                  required
                                  className={`!pt-[30px] !pb-2 bg-white font-medium border ${contactErrors.password ? 'border-red-500' : 'border-gray-300'} text-[#000] text-base leading-5 rounded-[4px] focus:ring-primary-500 focus:border-primary-500 block w-full p-[15px] dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500 dark:shadow-sm-light`}
                                />
                              </div>
                              {contactErrors.password && (
                                <p className="mt-1 text-sm text-red-500">{Array.isArray(contactErrors.password) ? contactErrors.password[0] : contactErrors.password}</p>
                              )}
                            </div>
                            <div className="w-full md:w-1/2 md:max-w-[400px]">
                              <div className="relative">  
                                <label htmlFor="password_confirmation" className="absolute top-0 pt-2.5 px-[15px] block text-xs text-[#656565] dark:text-white">Confirm Password <span className="text-red-500">*</span></label>
                                <input
                                  type="password"
                                  id="password_confirmation"
                                  name="password_confirmation"
                                  value={contactData.password_confirmation}
                                  onChange={handleContactChange}
                                  required
                                  className={`!pt-[30px] !pb-2 bg-white font-medium border ${contactErrors.password_confirmation ? 'border-red-500' : 'border-gray-300'} text-[#000] text-base leading-5 rounded-[4px] focus:ring-primary-500 focus:border-primary-500 block w-full p-[15px] dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500 dark:shadow-sm-light`}
                                />
                              </div>
                              {contactErrors.password_confirmation && (
                                <p className="mt-1 text-sm text-red-500">{Array.isArray(contactErrors.password_confirmation) ? contactErrors.password_confirmation[0] : contactErrors.password_confirmation}</p>
                              )}
                            </div>
                          </div>
                          <div className="mb-6">
                            <label className="flex items-start">
                              <input
                                type="checkbox"
                                checked={agreeToCreateAccount}
                                onChange={(e) => setAgreeToCreateAccount(e.target.checked)}
                                className="mt-1 mr-3 h-4 w-4 text-[#832EE6] focus:ring-[#832EE6] border-gray-300 rounded"
                                required
                              />
                              <span className="text-sm text-gray-700">
                                I agree that an account will be automatically created for me using the email address provided above.
                              </span>
                            </label>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex justify-between gap-4 mt-6">
                      <button
                        type="button"
                        onClick={() => {
                          setShowReview(false)
                          setIsSubmitting(false)
                          setIsPublishing(false)
                        }}
                        className="focus:outline-none min-w-[113px] text-[#832EE6] font-bold bg-white hover:bg-gray-50 border-2 border-[#832EE6] rounded-[19px] text-sm px-5 py-2.5"
                      >
                        BACK
                      </button>
                      <button
                        type="submit"
                        id="publish-ad-button"
                        disabled={isPublishing || (!auth.isLoggedIn && !agreeToCreateAccount)}
                        className="focus:outline-none min-w-[113px] text-white font-bold bg-[#832EE6] hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 rounded-[19px] text-sm px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isPublishing ? 'PUBLISHING...' : 'PUBLISH AD'}
                      </button>
                    </div>
                  </form>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Sticky Mobile Publish Button */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] md:hidden z-50">
          <button
            type="button"
            onClick={() => {
              const publishBtn = document.getElementById('publish-ad-button');
              if (publishBtn) publishBtn.click();
            }}
            disabled={isPublishing || (!auth.isLoggedIn && !agreeToCreateAccount)}
            className="w-full focus:outline-none text-white font-bold bg-[#832EE6] hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 rounded-[19px] text-base px-5 py-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            {isPublishing ? 'PUBLISHING...' : 'PUBLISH AD'}
          </button>
        </div>

        <Footer />
      </>
    )
  }

  // Form screen
  return (
    <>
      <Header />
      <div className='bg-[#F7F7F7] py-[20px] md:py-[35px] min-h-screen'>
        <div className='container mx-auto px-2 flex justify-center'>
          <div className="max-w-[953px] w-full py-[15px] md:py-[25px] bg-white border border-[#EBEBEB] rounded-lg mb-5" style={{ boxShadow: '0px 3px 6px #8D8D8D36' }}>
            <p className='my-auto uppercase font-bold text-[20px] md:text-[24px] leading-7 px-2 md:px-6 lg:px-10 pb-[15px] md:pb-[25px]'>Post an Ad</p>
            <hr />
            <form onSubmit={handleSubmit}>
              <div className='px-4 md:px-6 lg:px-10 pt-5 pb-2.5'>
                <p className="font-bold text-[15px] md:text-[17px] leading-6 pb-[25px] md:pb-[38px]">Choose an ad type</p>
                <div className="flex flex-col md:flex-row gap-4 mb-9">
                  {adOptions.ad_types && Object.entries(adOptions.ad_types).map(([key, value]) => (
                    <div key={key} className='relative flex w-full'>
                      <input
                        name="ad_type"
                        checked={formData.ad_type === key}
                        onChange={handleChange}
                        id={`radioType${key}`}
                        type="radio"
                        value={key}
                        className="peer w-4 h-4 rounded accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600 absolute top-1/2 -translate-y-1/2 left-5"
                      />
                      <label
                        htmlFor={`radioType${key}`}
                        className="py-3 px-5 rounded-lg cursor-pointer w-full border border-white hover:border-[#832EE6] hover:border peer-checked:!shadow-none peer-checked:border-[#832EE6]"
                        style={{ boxShadow: '0px 3px 6px #00000029' }}
                      >
                        <span className="text-base font-medium text-[#0D0D0D] dark:text-gray-300 pl-8">{value}</span>
                      </label>
                    </div>
                  ))}
                </div>
                {formData.ad_type === 'for-stud' && (
                  <>
                    <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4" role="alert">
                      <p>All options below refer to your cat. Fill in the fields related to your cat. Special breeding requirements should be filled in the "Special Breeding Requirements" field.</p>
                    </div>
                    <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4" role="alert">
                      <p className="font-bold mb-2">Stud Disclaimer</p>
                      <p className="mb-2">Stud services are offered for breeding purposes only.</p>
                      <p className="mb-2">Both male and female cats must be at least 12 months old and in good health.</p>
                      <p>SphynxPlace does not take responsibility for agreements made between users.</p>
                    </div>
                  </>
                )}
                <hr />
                <p className="font-bold text-[15px] md:text-[17px] leading-6 pb-[25px] md:pb-[38px] pt-[25px] md:pt-[36px]">
                  {formData.ad_type === 'wanted' ? 'Information about the pet you are looking for:' : 'Information about your pet:'}
                </p>
                <div className='flex flex-col md:flex-row justify-between gap-6 md:gap-1 mb-11'>
                  <div className='w-full md:max-w-[420px]'>
                    <div>
                      <label htmlFor="title" className="block mb-2 text-base font-medium text-[#0D0D0D] dark:text-white">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input 
                        name="title" 
                        value={formData.title || ''} 
                        onChange={handleChange} 
                        type="text" 
                        id="title" 
                        className={`max-h-[36px] bg-white border ${errors.title && (Array.isArray(errors.title) ? errors.title.length > 0 : true) ? 'border-red-500' : 'border-gray-300'} text-[#0D0D0D] text-sm rounded focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`} 
                      />
                      {errors.title && (Array.isArray(errors.title) ? errors.title.length > 0 : true) && (
                        <p className="mt-1 text-sm text-red-500">{Array.isArray(errors.title) ? errors.title[0] : errors.title}</p>
                      )}
                    </div>
                    {formData.ad_type !== 'for-adoption' && (
                      <>
                        <div htmlFor="price" className="block mt-5 mb-2 text-base font-medium text-[#0D0D0D] dark:text-white">
                          {formData.ad_type === 'for-stud' ? 'Stud Fee' : formData.ad_type === 'wanted' ? 'Budget' : 'Price'}
                        </div>
                        <div className="flex items-center">
                          <div className="relative max-w-[180px]">
                            <input 
                              name="price" 
                              value={formData.price || ''} 
                              onChange={handleChange} 
                              type="text" 
                              id="price" 
                              disabled={formData.price_negotiable} 
                              placeholder={formData.price_negotiable ? 'Negotiable' : (formData.ad_type === 'for-stud' ? 'Stud Fee' : formData.ad_type === 'wanted' ? 'Budget' : 'Price')}
                              className="max-h-[36px] pr-[60px] bg-white text-left border border-gray-300 text-[#0D0D0D] text-sm rounded focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            />
                            <div className='absolute right-0 top-0'>
                              <select 
                                name="currency" 
                                value={formData.currency || ''} 
                                onChange={handleChange} 
                                id="currency" 
                                disabled={formData.price_negotiable}
                                className="font-bold max-h-[36px] bg-white border border-gray-300 text-gray-900 text-base border-l-0 rounded-r border-s-gray-100 dark:border-s-gray-700 border-s-2 focus:ring-blue-500 focus:border-blue-500 block w-full p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                              >
                                {adOptions.currencies && Object.entries(adOptions.currencies).map(([key, value]) => (
                                  <option key={key} value={key}>{value}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              handleChange({
                                target: {
                                  name: 'price_negotiable',
                                  checked: !formData.price_negotiable,
                                  type: 'checkbox'
                                }
                              });
                              if (!formData.price_negotiable) {
                                handleChange({
                                  target: {
                                    name: 'price',
                                    value: '',
                                    type: 'text'
                                  }
                                });
                              }
                            }}
                            className={`ml-2 px-2 h-[36px] text-sm rounded border-2 border-purple-600 ${
                              formData.price_negotiable 
                                ? 'bg-purple-600 text-white' 
                                : 'bg-white text-gray-700'
                            } hover:bg-purple-700 hover:border-purple-700 hover:text-white transition-colors`}
                          >
                            Negotiable
                          </button>
                        </div>
                        {errors.price && (
                          <p className="mt-1 text-sm text-red-500">{Array.isArray(errors.price) ? errors.price[0] : errors.price}</p>
                        )}
                      </>
                    )}
                    {adOptions['color'] && (
                      <div className="mt-7 mb-2 text-base font-medium text-[#0D0D0D] dark:text-white">
                        Color {formData.ad_type === 'for-sale' && <span className="text-red-500">*</span>}
                        <div className="grid grid-cols-2 gap-2 -mt-2 max-w-[250px] pt-2">
                          {adOptions.color.options && Object.entries(adOptions.color.options).map(([key, value]) => (
                            <ColorBox 
                              key={key} 
                              name="color[]" 
                              type="checkbox" 
                              value={key} 
                              spanText={value} 
                              onChange={handleChange} 
                              checked={formData.color?.includes(key)}
                            />
                          ))}
                        </div>
                        {errors.color && (
                          <p className="mt-1 text-sm text-red-500">{Array.isArray(errors.color) ? errors.color[0] : errors.color}</p>
                        )}
                      </div>
                    )}
                    {adOptions['date-of-birth'] && (
                      <>
                        <label htmlFor="date-of-birth" className="block mt-5 mb-2 text-base font-medium text-[#0D0D0D] dark:text-white">
                          Date of Birth {['for-sale', 'for-adoption', 'for-stud'].includes(formData.ad_type) && <span className="text-red-500">*</span>}
                        </label>
                        <input 
                          name="date-of-birth" 
                          value={formData['date-of-birth'] || ''} 
                          onChange={handleChange} 
                          type="date" 
                          id="date-of-birth" 
                          max={formData.ad_type === 'for-stud' 
                            ? new Date(new Date().setMonth(new Date().getMonth() - 12)).toISOString().split('T')[0]
                            : new Date(new Date().setMonth(new Date().getMonth() - 2)).toISOString().split('T')[0]}
                          className={`max-h-[36px] max-w-[180px] bg-white border ${errors['date-of-birth'] && (Array.isArray(errors['date-of-birth']) ? errors['date-of-birth'].length > 0 : true) ? 'border-red-500' : 'border-gray-300'} text-[#0D0D0D] text-sm rounded focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
                        />
                        <span className="block mt-1 text-xs text-gray-500 md:hidden">(mm/dd/yyyy)</span>
                        {errors['date-of-birth'] && (Array.isArray(errors['date-of-birth']) ? errors['date-of-birth'].length > 0 : true) && (
                          <p className="mt-1 text-sm text-red-500">{Array.isArray(errors['date-of-birth']) ? errors['date-of-birth'][0] : errors['date-of-birth']}</p>
                        )}
                      </>
                    )}
                  </div>
                  <div className='w-full md:max-w-[380px]'>
                    {adOptions['litter-male-count'] && (
                      <>
                        <label htmlFor="litter-male-count" className="block text-base font-medium text-[#0D0D0D] dark:text-white">
                          {formData.ad_type === 'for-adoption' ? 'Cats for adoption' : formData.ad_type === 'for-stud' ? 'Stud males available' : formData.ad_type === 'wanted' ? 'Gender preference' : 'Pets in litter'}
                          {['for-sale', 'for-adoption', 'for-stud'].includes(formData.ad_type) && <span className="text-red-500">*</span>}
                        </label>
                        <div className="space-y-4 mt-2">
                          <div className="flex items-center">
                            <span className="text-base font-medium text-[#777777] w-[70px]">Male</span>
                            <div className="flex items-center">
                              <InputCounter 
                                name="litter-male-count"
                                value={formData['litter-male-count']}
                                onChange={handleChange}
                                step={1}
                              />
                            </div>
                          </div>
                          {adOptions['litter-female-count'] && formData.ad_type !== 'for-stud' && (
                            <div className="flex items-center">
                              <span className="text-base font-medium text-[#777777] w-[70px]">Female</span>
                              <div className="flex items-center">
                                <InputCounter
                                  name="litter-female-count" 
                                  value={formData['litter-female-count']}
                                  onChange={handleChange}
                                  step={1}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        {errors.gender && (
                          <p className="mt-1 text-sm text-red-500">{Array.isArray(errors.gender) ? errors.gender[0] : errors.gender}</p>
                        )}
                      </>
                    )}
                    {adOptions['energy-level'] && (
                      <>
                        <label htmlFor="energy-level" className="block text-base font-medium text-[#0D0D0D] dark:text-white mt-5 mb-2">Energy level</label>
                        <select 
                          id="energy-level" 
                          name="energy-level" 
                          value={formData['energy-level'] || ''} 
                          onChange={handleChange} 
                          className="bg-white border max-h-[36px] border-gray-300 text-[#777777] text-base rounded focus:ring-blue-500 focus:border-blue-500 block w-full h-full p-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        >
                          <option value="">Select energy level</option>
                          {adOptions['energy-level']?.options && Object.entries(adOptions['energy-level'].options).map(([key, value]) => (
                            <option key={key} value={key}>{value}</option>
                          ))}
                        </select>
                      </>
                    )}
                    {adOptions['home-lifestyle'] && (
                      <>
                        <label htmlFor="home-lifestyle" className="block text-base font-medium text-[#0D0D0D] dark:text-white mt-3 mb-2">Home lifestyle</label>
                        <select 
                          id="home-lifestyle" 
                          name="home-lifestyle" 
                          value={formData['home-lifestyle'] || ''} 
                          onChange={handleChange} 
                          className="bg-white border max-h-[36px] border-gray-300 text-[#777777] text-base rounded focus:ring-blue-500 focus:border-blue-500 block w-full h-full p-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        >
                          <option value="">Select home lifestyle</option>
                          {adOptions['home-lifestyle']?.options && Object.entries(adOptions['home-lifestyle'].options).map(([key, value]) => (
                            <option key={key} value={key}>{value}</option>
                          ))}
                        </select>
                      </>
                    )}
                    {adOptions['litter-habits'] && (
                      <>
                        <label htmlFor="litter-habits" className="block text-base font-medium text-[#0D0D0D] dark:text-white mt-3 mb-2">Litter habits</label>
                        <select 
                          id="litter-habits" 
                          name="litter-habits" 
                          value={formData['litter-habits'] || ''} 
                          onChange={handleChange} 
                          className="bg-white border max-h-[36px] border-gray-300 text-[#777777] text-base rounded focus:ring-blue-500 focus:border-blue-500 block w-full h-full p-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        >
                          <option value="">Select litter habits</option>
                          {adOptions['litter-habits']?.options && Object.entries(adOptions['litter-habits'].options).map(([key, value]) => (
                            <option key={key} value={key}>{value}</option>
                          ))}
                        </select>
                      </>
                    )}
                    {adOptions['grooming-level'] && (
                      <>
                        <label htmlFor="grooming-level" className="block text-base font-medium text-[#0D0D0D] dark:text-white mt-3 mb-2">Grooming level</label>
                        <select 
                          id="grooming-level" 
                          name="grooming-level" 
                          value={formData['grooming-level'] || ''} 
                          onChange={handleChange} 
                          className="bg-white border max-h-[36px] border-gray-300 text-[#777777] text-base rounded focus:ring-blue-500 focus:border-blue-500 block w-full h-full p-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        >
                          <option value="">Select grooming level</option>
                          {adOptions['grooming-level']?.options && Object.entries(adOptions['grooming-level'].options).map(([key, value]) => (
                            <option key={key} value={key}>{value}</option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                </div>

                {adOptions['health-and-documentation'] && (
                  <>
                    <hr />
                    <p className="font-bold text-[15px] md:text-[17px] leading-6 pb-[25px] md:pb-[33px] pt-[25px] md:pt-[36px]">Cat health & Documentation</p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 mb-10">
                      {adOptions['health-and-documentation']?.options && Object.entries(adOptions['health-and-documentation'].options).map(([key, value], index) => (
                        <li key={index}>
                          <div className="flex items-center mr-4">
                            <input name="health-and-documentation[]" onChange={handleChange} id={`health-checkbox-${index}`} type="checkbox" value={key} checked={formData['health-and-documentation']?.includes(key)} className="w-4 h-4 accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600" />
                            <label htmlFor={`health-checkbox-${index}`} className="ml-2 text-base font-medium text-gray-900 dark:text-gray-300">{value}</label>
                          </div>
                        </li>
                      ))}
                      <li>
                        <div className="flex items-center mr-4">
                          <input name="pedigree[]" onChange={handleChange} id="pedigree-checkbox" type="checkbox" value="pedigree" checked={formData['pedigree']?.includes('pedigree')} className="w-4 h-4 accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600" />
                          <label htmlFor="pedigree-checkbox" className="ml-2 text-base font-medium text-gray-900 dark:text-gray-300">Pedigree available</label>
                        </div>
                      </li>
                    </ul>
                  </>
                )}

                <hr />
                <p id="media-upload-section" className="font-bold text-[15px] md:text-[17px] leading-6 pb-[25px] md:pb-10 pt-[25px] md:pt-[36px]">Upload media</p>
                <div className="flex flex-col md:flex-row gap-6 md:gap-10 mb-2">
                  <ImageUpload ref={imageUploadRef} initialImage={formData.featured_image_url} onImageChange={handleImageChange} onUploadingChange={setIsImageUploading} />
                  <div className="hidden md:block w-[1px] bg-[#DEDEDE]"></div>
                  <MultipleImageUpload ref={galleryUploadRef} initialImages={formData.gallery} onImagesChange={handleGalleryChange} onUploadingChange={setIsGalleryUploading} />
                </div>
                {errors.images && (
                  <p className="mt-1 mb-10 text-sm text-red-500">{errors.images[0]}</p>
                )}
                {contactErrors.images && (
                   <p className="mt-1 mb-10 text-sm text-red-500">{contactErrors.images[0]}</p>
                )}

                <hr />
                {(adOptions.temperament || adOptions.compatibility) && (
                  <>
                    <p className="font-bold text-[15px] md:text-[17px] leading-6 pb-[25px] md:pb-[33px] pt-[25px] md:pt-[36px]">Cat Characteristics:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-2 mb-10">
                      {adOptions.temperament && (
                        <div>
                          <p className="uppercase text-base mb-6">Temperament</p>
                          <ul className="space-y-5 mb-10">
                            {adOptions.temperament?.options && Object.entries(adOptions.temperament.options).map(([key, value], index) => (
                              <li key={index}>
                                <div className="flex items-center mr-4">
                                  <input name="temperament[]" onChange={handleChange} id={`temperament-checkbox-${index}`} type="checkbox" value={key} checked={formData.temperament?.includes(key)} className="w-4 h-4 accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600" />
                                  <label htmlFor={`temperament-checkbox-${index}`} className="ml-2 text-base font-medium text-gray-900 dark:text-gray-300">{value}</label>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {adOptions.compatibility && (
                        <div>
                          <p className="uppercase text-base mb-6">Compatibility</p>
                          <ul className="space-y-5 mb-10">
                            {adOptions.compatibility?.options && Object.entries(adOptions.compatibility.options).map(([key, value], index) => (
                              <li key={index}>
                                <div className="flex items-center mr-4">
                                  <input name="compatibility[]" onChange={handleChange} id={`compatibility-checkbox-${index}`} type="checkbox" value={key} checked={formData.compatibility?.includes(key)} className="w-4 h-4 accent-purple-600 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600" />
                                  <label htmlFor={`compatibility-checkbox-${index}`} className="ml-2 text-base font-medium text-gray-900 dark:text-gray-300">{value}</label>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <hr />
                <p className="font-bold text-[17px] leading-6 pb-[23px] pt-[36px]">Additional information:</p>
                <textarea name="description" value={formData.description || ''} onChange={handleChange} id="description" rows="8" className="mb-9 block p-2.5 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"></textarea>

                <div className="flex justify-between">
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="focus:outline-none min-w-[113px] text-white font-bold bg-[#832EE6] hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 rounded-[19px] text-sm px-5 py-2.5 mb-2 dark:bg-purple-600 dark:hover:bg-purple-700 dark:focus:ring-purple-900 max-h-[37px] flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>SUBMITTING...</span>
                      </>
                    ) : 'SUBMIT'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}

export default PostAd
